import { onUnmounted, watch } from 'vue'
import { getEcho } from '@/services/echo'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useStudioStore } from '@/modules/studio/stores/studio.store'
import type {
  WhatsappConversationEscalatedEvent,
  WhatsappConversationUpdatedEvent,
  WhatsappMessageReceivedEvent,
} from '../types'

export type StudioChannelHandlers = {
  /** Se emite cuando llega un mensaje nuevo al studio */
  onMessageReceived?: (event: WhatsappMessageReceivedEvent) => void
  /** Se emite cuando se actualiza estado/modo de conversación */
  onConversationUpdated?: (event: WhatsappConversationUpdatedEvent) => void
  /** Se emite cuando una conversación se escala a humano */
  onConversationEscalated?: (event: WhatsappConversationEscalatedEvent) => void
}

/**
 * Suscribe al canal privado `studio.{studioId}` de Laravel Echo/Pusher.
 * Limpia la suscripción al desmontar el componente o cuando cambia el studioId.
 */
export function useStudioChannel(handlers: StudioChannelHandlers) {
  const authStore  = useAuthStore()
  const studioStore = useStudioStore()

  let currentChannelName: string | null = null

  function subscribe(studioId: string) {
    const token = authStore.token
    const role = authStore.role
    if (!token || !studioId || !role) return
    if (!['owner', 'admin', 'staff', 'coach'].includes(role)) return

    const echo = getEcho(token, studioId)
    const channelName = `studio.${studioId}`
    if (currentChannelName === channelName) return
    currentChannelName = channelName

    const channel = echo.private(channelName)

    if (handlers.onMessageReceived) {
      channel.listen('.whatsapp.message.received', (e: WhatsappMessageReceivedEvent) => {
        handlers.onMessageReceived!(e)
      })
    }

    if (handlers.onConversationUpdated) {
      channel.listen('.whatsapp.conversation.updated', (e: WhatsappConversationUpdatedEvent) => {
        handlers.onConversationUpdated!(e)
      })
    }

    if (handlers.onConversationEscalated) {
      channel.listen('.whatsapp.conversation.escalated', (e: WhatsappConversationEscalatedEvent) => {
        handlers.onConversationEscalated!(e)
      })
    }
  }

  function unsubscribe() {
    if (!currentChannelName) return
    const token = authStore.token
    const studioId = studioStore.currentStudioId
    if (!token || !studioId) return

    try {
      const echo = getEcho(token, studioId)
      // Compatibilidad: algunos adapters esperan nombre sin prefijo y otros con `private-`.
      echo.leave(currentChannelName)
      echo.leave(`private-${currentChannelName}`)
    } catch {
      // Si Echo ya fue destruido, no hay nada que limpiar
    }
    currentChannelName = null
  }

  // Re-suscribir si cambia el studio (cambio multi-tenant)
  watch(
    () => [studioStore.currentStudioId, authStore.role, authStore.token] as const,
    ([newId], [oldId]) => {
      if (oldId) unsubscribe()
      if (newId) subscribe(newId)
    },
  )

  // Suscripción inicial
  if (studioStore.currentStudioId) {
    subscribe(studioStore.currentStudioId)
  }

  onUnmounted(unsubscribe)
}
